/**
 * BATCH REQUEST CONTROLLER
 * Centralized API request batching and rate limiting for Zoom API calls
 * 
 * PURPOSE: Prevent 429 (Too Many Requests) errors by intelligently batching
 * API calls across multiple components with smart caching and priority queuing.
 * 
 * FEATURES:
 * - Smart batching (5 meetings/batch, 300ms delays)
 * - Priority-based processing (VIP meetings first)
 * - Shared cache between VIP monitoring and regular polling
 * - Rate limiting: Never exceed 3 requests/second
 * - Automatic cache invalidation with TTL
 * - Request deduplication
 */

import debugService from './debugService';

class BatchRequestController {
  constructor() {
    // Cache configuration
    this.cache = new Map();
    this.CACHE_TTL = 10000; // 10 seconds TTL (reduced requests, ~11s latency)
    this.ERROR_CACHE_TTL = 3000; // Shorter TTL for error responses
    
    // Batch configuration
    this.BATCH_SIZE = 5; // Process 5 meetings per batch
    this.BATCH_DELAY = 300; // 300ms delay between batches
    this.MAX_REQUESTS_PER_SECOND = 3; // Rate limit
    
    // Request queue and tracking
    this.requestQueue = [];
    this.processingQueue = false;
    this.lastRequestTime = 0;
    this.requestsThisSecond = 0;
    this.secondStartTime = Date.now();
    
    // Pending requests (for deduplication)
    this.pendingRequests = new Map();
    
    // 🔄 Circuit Breaker for consecutive errors
    this.errorTracker = {
      consecutive429Errors: 0,
      lastErrorTime: 0,
      backoffUntil: 0,
      maxConsecutiveErrors: 5,
      backoffDuration: 30000, // 30 seconds backoff on circuit break
    };
    
    // Statistics
    this.stats = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      apiCalls: 0,
      batchesProcessed: 0,
      rateLimitPrevented: 0,
      errors429: 0,
      circuitBreaks: 0,
      cacheCleanups: 0
    };
    
    debugService.log('✅ BatchRequestController initialized with Circuit Breaker');
  }
  
  /**
   * Get cache key for a request
   */
  getCacheKey(type, id) {
    return `${type}-${id}`;
  }
  
  /**
   * Check if cached data is still valid (respects custom TTL for errors)
   */
  isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    const age = Date.now() - cacheEntry.timestamp;
    const ttl = cacheEntry.ttl || this.CACHE_TTL; // Use custom TTL if set
    return age < ttl;
  }
  
  /**
   * Get data from cache
   */
  getFromCache(type, id) {
    const key = this.getCacheKey(type, id);
    const cached = this.cache.get(key);
    
    if (cached && this.isCacheValid(cached)) {
      this.stats.cacheHits++;
      return cached.data;
    }
    
    this.stats.cacheMisses++;
    return null;
  }
  
  /**
   * Store data in cache
   */
  storeInCache(type, id, data, isError = false) {
    const key = this.getCacheKey(type, id);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      isError, // Mark error responses for shorter TTL
      ttl: isError ? this.ERROR_CACHE_TTL : this.CACHE_TTL
    });
  }
  
  /**
   * 🔄 Circuit Breaker: Check if we should pause requests
   */
  isCircuitOpen() {
    const now = Date.now();
    
    // Check if we're in backoff period
    if (this.errorTracker.backoffUntil > now) {
      debugService.log(`⚡ Circuit OPEN - backing off until ${new Date(this.errorTracker.backoffUntil).toLocaleTimeString()}`);
      return true;
    }
    
    // Reset if backoff expired
    if (this.errorTracker.backoffUntil > 0 && this.errorTracker.backoffUntil <= now) {
      debugService.log('✅ Circuit CLOSED - resuming normal operation');
      this.errorTracker.consecutive429Errors = 0;
      this.errorTracker.backoffUntil = 0;
    }
    
    return false;
  }
  
  /**
   * 🔄 Record a 429 error and trigger circuit breaker if needed
   */
  record429Error() {
    this.stats.errors429++;
    this.errorTracker.consecutive429Errors++;
    this.errorTracker.lastErrorTime = Date.now();
    
    debugService.log(`⚠️ 429 Error #${this.errorTracker.consecutive429Errors} (threshold: ${this.errorTracker.maxConsecutiveErrors})`);
    
    // Trip circuit breaker if too many consecutive errors
    if (this.errorTracker.consecutive429Errors >= this.errorTracker.maxConsecutiveErrors) {
      this.errorTracker.backoffUntil = Date.now() + this.errorTracker.backoffDuration;
      this.stats.circuitBreaks++;
      
      console.error(`🚨 CIRCUIT BREAKER TRIPPED - Too many 429 errors! Backing off for ${this.errorTracker.backoffDuration / 1000}s`);
      
      // Clear error cache to force fresh requests after backoff
      this.clearErrorCache();
    }
  }
  
  /**
   * 🔄 Reset error counter on successful request
   */
  recordSuccess() {
    if (this.errorTracker.consecutive429Errors > 0) {
      debugService.log(`✅ Success after ${this.errorTracker.consecutive429Errors} errors - resetting counter`);
      this.errorTracker.consecutive429Errors = 0;
    }
  }
  
  /**
   * 🗑️ Clear only error responses from cache
   */
  clearErrorCache() {
    let cleared = 0;
    for (const [key, value] of this.cache.entries()) {
      if (value.isError) {
        this.cache.delete(key);
        cleared++;
      }
    }
    this.stats.cacheCleanups++;
    debugService.log(`🗑️ Cleared ${cleared} error entries from cache`);
  }
  
  /**
   * Rate limiting check - ensures we don't exceed MAX_REQUESTS_PER_SECOND
   */
  async enforceRateLimit() {
    const now = Date.now();
    
    // Reset counter every second
    if (now - this.secondStartTime >= 1000) {
      this.secondStartTime = now;
      this.requestsThisSecond = 0;
    }
    
    // If we've hit the limit, wait
    if (this.requestsThisSecond >= this.MAX_REQUESTS_PER_SECOND) {
      const waitTime = 1000 - (now - this.secondStartTime);
      if (waitTime > 0) {
        this.stats.rateLimitPrevented++;
        debugService.log(`⏱️ Rate limit: waiting ${waitTime}ms`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        this.secondStartTime = Date.now();
        this.requestsThisSecond = 0;
      }
    }
    
    this.requestsThisSecond++;
    this.lastRequestTime = Date.now();
  }
  
  /**
   * Request participants data for a meeting (returns promise)
   */
  async requestParticipants(meetingId, priority = 5, apiFunction) {
    this.stats.totalRequests++;
    
    // Check cache first
    const cached = this.getFromCache('participants', meetingId);
    if (cached) {
      return cached;
    }
    
    // Check if request already pending (deduplication)
    const pendingKey = this.getCacheKey('participants', meetingId);
    if (this.pendingRequests.has(pendingKey)) {
      debugService.log(`⏳ Request for meeting ${meetingId} already pending, waiting...`);
      return this.pendingRequests.get(pendingKey);
    }
    
    // Create new request promise
    const requestPromise = new Promise((resolve, reject) => {
      this.requestQueue.push({
        type: 'participants',
        id: meetingId,
        priority,
        apiFunction,
        resolve,
        reject
      });
      
      // Start processing if not already running
      if (!this.processingQueue) {
        this.processQueue();
      }
    });
    
    // Store as pending
    this.pendingRequests.set(pendingKey, requestPromise);
    
    // Clean up after resolution
    requestPromise.finally(() => {
      this.pendingRequests.delete(pendingKey);
    });
    
    return requestPromise;
  }
  
  /**
   * Request group students data
   */
  async requestGroupStudents(groupId, priority = 5, apiFunction) {
    this.stats.totalRequests++;
    
    // Check cache first
    const cached = this.getFromCache('students', groupId);
    if (cached) {
      return cached;
    }
    
    // Check if request already pending
    const pendingKey = this.getCacheKey('students', groupId);
    if (this.pendingRequests.has(pendingKey)) {
      return this.pendingRequests.get(pendingKey);
    }
    
    const requestPromise = new Promise((resolve, reject) => {
      this.requestQueue.push({
        type: 'students',
        id: groupId,
        priority,
        apiFunction,
        resolve,
        reject
      });
      
      if (!this.processingQueue) {
        this.processQueue();
      }
    });
    
    this.pendingRequests.set(pendingKey, requestPromise);
    requestPromise.finally(() => {
      this.pendingRequests.delete(pendingKey);
    });
    
    return requestPromise;
  }
  
  /**
   * Process the request queue in batches
   */
  async processQueue() {
    if (this.processingQueue) return;
    
    this.processingQueue = true;
    
    try {
      while (this.requestQueue.length > 0) {
        // 🔄 Check circuit breaker before processing
        if (this.isCircuitOpen()) {
          debugService.log('⚡ Circuit breaker OPEN - pausing queue processing');
          // Return empty data for all pending requests
          const remainingRequests = this.requestQueue.splice(0);
          remainingRequests.forEach(request => {
            const emptyData = request.type === 'participants' 
              ? { participants: [], count: 0, success: false, error: 'Circuit breaker open - rate limit protection active' }
              : { data: [], count: 0, success: false, error: 'Circuit breaker open - rate limit protection active' };
            request.resolve(emptyData);
          });
          break;
        }
        
        // Sort queue by priority (lower number = higher priority)
        this.requestQueue.sort((a, b) => a.priority - b.priority);
        
        // Take a batch
        const batch = this.requestQueue.splice(0, this.BATCH_SIZE);
        
        debugService.log(`📦 Processing batch of ${batch.length} requests (${this.requestQueue.length} remaining in queue)`);
        
        // Process batch with delays between requests
        for (let i = 0; i < batch.length; i++) {
          const request = batch[i];
          
          try {
            // Enforce rate limiting
            await this.enforceRateLimit();
            
            // Make API call
            debugService.log(`🔄 API call: ${request.type} ${request.id} (priority: ${request.priority})`);
            const response = await request.apiFunction();
            
            this.stats.apiCalls++;
            
            // Extract data based on type
            let data;
            if (request.type === 'participants') {
              data = response.data || response;
            } else if (request.type === 'students') {
              data = response.data || response;
            }
            
            // ✅ Record success (resets error counter)
            this.recordSuccess();
            
            // Store in cache (not an error)
            this.storeInCache(request.type, request.id, data, false);
            
            // Resolve promise
            request.resolve(data);
            
            // Small delay between requests in batch (except last one)
            if (i < batch.length - 1) {
              await new Promise(resolve => setTimeout(resolve, this.BATCH_DELAY));
            }
            
          } catch (error) {
            const status = error.response?.status;
            
            // 🔄 Track 429 errors for circuit breaker
            if (status === 429) {
              console.error(`⚠️ 429 Rate Limit Error: ${request.type} ${request.id}`);
              this.record429Error();
              
              // If circuit just opened, stop processing this batch
              if (this.isCircuitOpen()) {
                debugService.log('⚡ Circuit breaker TRIPPED during batch - stopping');
                // Return empty data for remaining requests in this batch
                for (let j = i; j < batch.length; j++) {
                  const remainingRequest = batch[j];
                  const emptyData = remainingRequest.type === 'participants' 
                    ? { participants: [], count: 0, success: false, error: 'Circuit breaker tripped' }
                    : { data: [], count: 0, success: false, error: 'Circuit breaker tripped' };
                  remainingRequest.resolve(emptyData);
                }
                break;
              }
            } else {
              console.error(`❌ API call failed: ${request.type} ${request.id}`, error.message);
            }
            
            // Return empty data on error instead of rejecting
            const emptyData = request.type === 'participants' 
              ? { participants: [], count: 0, success: false, error: error.message, status }
              : { data: [], count: 0, success: false, error: error.message, status };
            
            // Store empty result in cache with error flag (shorter TTL)
            this.storeInCache(request.type, request.id, emptyData, true);
            
            request.resolve(emptyData);
          }
        }
        
        this.stats.batchesProcessed++;
        
        // Delay before next batch (only if more items in queue)
        if (this.requestQueue.length > 0) {
          debugService.log(`⏸️ Batch delay: ${this.BATCH_DELAY}ms before next batch`);
          await new Promise(resolve => setTimeout(resolve, this.BATCH_DELAY));
        }
      }
    } finally {
      this.processingQueue = false;
      debugService.log(`✅ Queue processing complete`);
    }
  }
  
  /**
   * Batch request for multiple meetings (used by global monitoring)
   */
  async requestMultipleParticipants(meetingDataArray, apiFunction) {
    debugService.log(`📦 Batch request for ${meetingDataArray.length} meetings`);
    
    const promises = meetingDataArray.map(({ meetingId, priority }) =>
      this.requestParticipants(meetingId, priority || 5, () => apiFunction(meetingId))
    );
    
    return Promise.all(promises);
  }
  
  /**
   * Clear cache (useful for testing or manual refresh)
   */
  clearCache() {
    this.cache.clear();
    debugService.log('🗑️ Cache cleared');
  }
  
  /**
   * Get statistics
   */
  getStats() {
    const cacheSize = this.cache.size;
    const queueSize = this.requestQueue.length;
    const cacheHitRate = this.stats.totalRequests > 0
      ? ((this.stats.cacheHits / this.stats.totalRequests) * 100).toFixed(1)
      : 0;
    
    return {
      ...this.stats,
      cacheSize,
      queueSize,
      cacheHitRate: `${cacheHitRate}%`,
      pendingRequests: this.pendingRequests.size
    };
  }
  
  /**
   * Log statistics (useful for debugging)
   */
  logStats() {
    const stats = this.getStats();
    debugService.log('📊 Batch Controller Stats:', stats);
  }
}

// Singleton instance
const batchRequestController = new BatchRequestController();

export default batchRequestController;

